import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { AutocompleteComponent } from "../../../components/autocomplete/autocomplete.component";
import { DragNDropComponent } from "../../../components/drag-n-drop/drag-n-drop.component";
import { Router } from "@angular/router";
import { DialogService } from "../../../services/dialog.service";
import { TranslateService } from "@ngx-translate/core";
import {
  Claim,
  Diagnosis,
  diagnosisCategories,
  DiagnosisDTO,
  FnolSubmissionResponse,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  PetInsuranceData
} from "../../../models";
import { DataStorageService } from "../../../services/data-storage.service";
import {
  SelectPetCardsModalComponent
} from "../../../components/select-pet-cards-modal/select-pet-cards-modal.component";
import { map, Observable } from "rxjs";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { hashFields } from "../../../utils";
import { ApiHttpService } from "../../../services/api-http.service";
import { FileInfo } from "../../../models";
import { LoadingService } from "../../../services/loading.service";

const FNOL_RELOAD_DELAY_MS = 3000;
const MAX_UPLOAD_SIZE_BYTES = 4.5 * 1024 * 1024; // Max 4.5 MB due to AWS Lambda restriction
const BLOCKED_UPLOAD_EXTENSIONS = ['.zip', '.tar', '.7z', '.rar'];

@Component({
  selector: 'cpf-pet-fnol',
  templateUrl: './pet-fnol.component.html',
  styleUrls: ['./pet-fnol.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PetFnolComponent implements OnInit {
  @ViewChild('vetInvoice', { static: false }) vetInvoice!: DragNDropComponent;
  @ViewChild('petInfoFormElement', { static: true }) petInfoFormElement!: ElementRef;
  @ViewChild(AutocompleteComponent, { static: false }) diagnosisAutocompleteComponent!: AutocompleteComponent;

  @Input()
  showBackButton: boolean = true;
  @Input()
  isDesktop = false;

  @Output() setIbanClicked = new EventEmitter<void>();

  selectedPets: PetInsuranceData[] = [];
  currentPet: PetInsuranceData | undefined;

  maxDate: any = new Date();
  petInfoForm = new FormGroup({
    conditionPreviouslyTreated: new FormControl(false, [this.validateConditionPreviouslyTreated()]),
    dateConditionNoticed: new FormControl(null, [this.validateConditionNoticed()]),
    dateConditionFirstTreated: new FormControl(null, [this.validateConditionFirstTreated()]),
    thirdPartyFault: new FormControl(false, [Validators.required]),
    additionalInformation: new FormControl(null),
    diagnosisAdditionalInformation: new FormControl(null, [this.validateDiagnosisAdditionalInformation()]),
    accidentDateField: new FormControl(null, [this.validateAccidentDateField()]),
    isItFollowUp: new FormControl(false),
    precedentClaimId: new FormControl<string | Claim>("", [this.validatePrecedentClaimId()]),
  })

  dataToSubmit: {
    vetInvoice: FileInfo;
    petsData: Array<any>;
  } | undefined

  precedentClaimsList: Array<Claim> = [];

  showFileSizeError = false;

  showFileTypeError = false

  // Exposed so the upload component can warn about the budget while files are being added,
  // instead of only failing validation once the user hits "next".
  maxUploadSizeBytes = MAX_UPLOAD_SIZE_BYTES;

  showCancelButton = false;

  exampleInvoiceImageSrc = this.getExampleInvoiceImagePath('de') // just a default initalization

  private initStateHash = '';

  constructor(private readonly router: Router,
              private dialogService: DialogService,
              private elementRef: ElementRef,
              private translate: TranslateService,
              private dataStorageService: DataStorageService,
              private apiHttpService: ApiHttpService,
              private loadingService: LoadingService) {
  }

  ngOnInit(): void {
    this.updateExampleInvoiceImageSrc(this.translate.getCurrentLang() ?? '')
    this.translate.onLangChange.subscribe((lc: { lang: string; }) => {
      this.updateExampleInvoiceImageSrc(lc.lang)
    });

    const activePet = this.dataStorageService.getAllData.find(pet => pet.full_contract_number === this.dataStorageService.selectedData?.full_contract_number);

    if (activePet) {
      // Find newer contract by base number
      const newerContract = this.dataStorageService.getAllData.find(c => c.base_number === activePet.base_number);
      this.selectedPets = [newerContract || activePet];
    }

    this.initStateHash = this.hashPetFnolFields();

    this.wireManualInputRevalidation();

    if (!this.dataStorageService.user?.iban) {
      this.showNoIbanSet();
      return;
    }

    if (this.dataStorageService.getAllData.length === 1) {
      this.selectedPets = this.dataStorageService.getAllData;
    } else {
      setTimeout(() => {
        this.addPet();
      }, 200);
    }

    this.initStateHash = this.hashPetFnolFields();

    setTimeout(() => {
      this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  showNoIbanSet() {
    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: this.translate.instant('pet-details.modals.iban-not-set.title'),
        message: this.translate.instant('pet-details.modals.iban-not-set.message'),
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: this.translate.instant('pet-details.modals.iban-not-set.set-iban-button'),
        cancelButton: GeneralConfirmationModalResult.Cancel
      } as GeneralConfirmationModalData
    }).afterClosed().subscribe((result: GeneralConfirmationModalResult) => {
      if (result === GeneralConfirmationModalResult.Confirm) {
        if (this.setIbanClicked.observed) {
          this.setIbanClicked.emit();
        } else {
          this.router.navigate(['calingo', 'account', 'change-iban']);
        }
      } else {
        this.navigateBack();
      }
    })
  }

  hashPetFnolFields() {
    const dataToHash = {
      currentPet: this.currentPet?.full_contract_number,
      conditionPreviouslyTreated: this.petInfoForm.get('conditionPreviouslyTreated')?.value,
      dateConditionNoticed: this.petInfoForm.get('dateConditionNoticed')?.value,
      dateConditionFirstTreated: this.petInfoForm.get('dateConditionFirstTreated')?.value,
      thirdPartyFault: this.petInfoForm.get('thirdPartyFault')?.value,
      additionalInformation: this.petInfoForm.get('additionalInformation')?.value,
      diagnosisAdditionalInformation: this.petInfoForm.get('diagnosisAdditionalInformation')?.value,
      accidentDateField: this.petInfoForm.get('accidentDateField')?.value,
      selectedPets: this.selectedPets.map(sP => sP.full_contract_number),
      vetInvoice: this.vetInvoice ? this.vetInvoice.fileList.map(f => f.name) : [],
    };

    return hashFields(dataToHash);
  }

  isFormChanged() {
    return this.hashPetFnolFields() !== this.initStateHash;
  }

  showAddPet() {
    return this.selectedPets.length === 0;
  }

  getExampleInvoiceImagePath(lang: string): string {
    if (lang === 'en') {
      return 'assets/example_invoices/E_Rechnung.png'
    }
    if (lang === 'fr') {
      return 'assets/example_invoices/F_Rechnung.png'
    }
    if (lang === 'it') {
      return 'assets/example_invoices/I_Rechnung.png'
    }
    return 'assets/example_invoices/D_Rechnung.png'
  }

  updateExampleInvoiceImageSrc(lang: string) {
    this.exampleInvoiceImageSrc = this.getExampleInvoiceImagePath(lang);
  }

  private hasBlockedFileType(): boolean {
    return !!this.vetInvoice?.fileList.some(f =>
      BLOCKED_UPLOAD_EXTENSIONS.some(extension => f.name.toLowerCase().endsWith(extension)));
  }

  private isOverFileSizeLimit(): boolean {
    return (this.vetInvoice?.filesSize ?? 0) > MAX_UPLOAD_SIZE_BYTES;
  }

  validateFirstStep() {
    if (this.selectedPets.length === 0 || !this.vetInvoice || this.vetInvoice.fileList.length === 0) {
      return false;
    }

    return !this.hasBlockedFileType() && !this.isOverFileSizeLimit();
  }

  validateForm() {
    this.petInfoForm.markAllAsTouched();
    this.diagnosisAutocompleteComponent?.autocompleteInputCtrl.markAllAsTouched();
    this.petInfoForm.get('precedentClaimId')?.updateValueAndValidity();
    this.petInfoForm.get('diagnosisAdditionalInformation')?.updateValueAndValidity();
    this.petInfoForm.get('accidentDateField')?.updateValueAndValidity();
    this.petInfoForm.get('dateConditionNoticed')?.updateValueAndValidity();
    this.petInfoForm.get('conditionPreviouslyTreated')?.updateValueAndValidity();
    this.petInfoForm.get('dateConditionFirstTreated')?.updateValueAndValidity();

    if (this.petInfoForm.invalid || this.diagnosisAutocompleteComponent.autocompleteInputCtrl.invalid) {
      this.scrollToFirstInvalidControl();
      return false;
    }

    return true;
  }

  showDiagnosisAdditionalInformation() {
    return this.diagnosisAutocompleteComponent && this.diagnosisAutocompleteComponent.autocompleteInputCtrl.valid
      && this.diagnosisAutocompleteComponent.selectedValues.length > 0
      && !!this.diagnosisAutocompleteComponent.selectedValues.find(diagnosis => this.findDiagnosisById(diagnosis.id, diagnosis.category)?.showAdditionalInformation);
  }

  showAccidentDateField() {
    return this.diagnosisAutocompleteComponent && this.diagnosisAutocompleteComponent.autocompleteInputCtrl.valid
      && this.diagnosisAutocompleteComponent.selectedValues.length > 0
      && !!this.diagnosisAutocompleteComponent.selectedValues.find(diagnosis => this.findDiagnosisById(diagnosis.id, diagnosis.category)?.category === 'ACCIDENT');
  }

  showFieldsForNotOnlyPreventionsOrAccident() {
    return this.diagnosisAutocompleteComponent && this.diagnosisAutocompleteComponent.autocompleteInputCtrl.valid
      && this.diagnosisAutocompleteComponent.selectedValues.length > 0
      && !(this.diagnosisAutocompleteComponent.selectedValues.filter(diagnosis => this.findDiagnosisById(diagnosis.id, diagnosis.category)?.category === 'PREVENTION' || this.findDiagnosisById(diagnosis.id, diagnosis.category)?.category === 'ACCIDENT').length === this.diagnosisAutocompleteComponent.selectedValues.length);
  }

  showFieldsForOnlyNotPreventions() {
    return this.diagnosisAutocompleteComponent && this.diagnosisAutocompleteComponent.autocompleteInputCtrl.valid
      && this.diagnosisAutocompleteComponent.selectedValues.length > 0
      && this.diagnosisAutocompleteComponent.selectedValues.filter(diagnosis => this.findDiagnosisById(diagnosis.id, diagnosis.category)?.category !== 'PREVENTION').length > 0;
  }

  showFieldsManualInput() {
    // Manual (classical-FNOL) fields apply whenever the claim is NOT a follow-up of an
    // existing Calingo claim: either the user answered "No" to the follow-up question, or
    // they answered "Yes" but their precedent claim isn't in the dropdown ("None of the
    // above"). Only a real precedent claim selected from the list suppresses these fields.
    return this.petInfoForm?.get('isItFollowUp')?.value === false
      || this.petInfoForm?.get('precedentClaimId')?.value === 'no-selected';
  }

  onlyAccidentDiagnosisSelected() {
    return this.diagnosisAutocompleteComponent && this.diagnosisAutocompleteComponent.autocompleteInputCtrl.valid
      && this.diagnosisAutocompleteComponent.selectedValues.length > 0
      && this.diagnosisAutocompleteComponent.selectedValues.filter(diagnosis => this.findDiagnosisById(diagnosis.id, diagnosis.category)?.category === 'ACCIDENT').length === this.diagnosisAutocompleteComponent.selectedValues.length;
  }

  onlyPreventionOrIllnessDiagnosisSelected() {
    return this.diagnosisAutocompleteComponent && this.diagnosisAutocompleteComponent.autocompleteInputCtrl.valid
      && this.diagnosisAutocompleteComponent.selectedValues.length > 0
      && this.diagnosisAutocompleteComponent.selectedValues.filter(diagnosis => this.findDiagnosisById(diagnosis.id, diagnosis.category)?.category === 'PREVENTION' || this.findDiagnosisById(diagnosis.id, diagnosis.category)?.category === 'ILLNESS').length === this.diagnosisAutocompleteComponent.selectedValues.length;
  }

  validatePrecedentClaimId(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.petInfoForm?.get('isItFollowUp')?.value === true) {
        return control.value ? null : { required: true }
      }

      return null;
    }
  }

  validateConditionPreviouslyTreated(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.showFieldsManualInput() && this.showFieldsForNotOnlyPreventionsOrAccident()) {
        return control.value !== undefined && control.value !== null ? null : { required: true }
      }

      return null;
    }
  }

  validateConditionNoticed(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.showFieldsManualInput() || !this.showFieldsForNotOnlyPreventionsOrAccident() || this.onlyAccidentDiagnosisSelected()) {
        return null;
      }
      return control.value ? null : { required: true }
    }
  }

  validateConditionFirstTreated(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.showFieldsManualInput() || this.petInfoForm?.get('conditionPreviouslyTreated')?.value !== true) {
        console.debug('validator validateConditionFirstTreated NOT active')
        console.debug("this.petInfoForm?.get('conditionPreviouslyTreated')?.value", this.petInfoForm?.get('conditionPreviouslyTreated')?.value)
        return null;
      }
      console.debug('validator validateConditionFirstTreated active')
      console.debug("this.petInfoForm?.get('conditionPreviouslyTreated')?.value", this.petInfoForm?.get('conditionPreviouslyTreated')?.value)
      return control.value ? null : { required: true }

    }
  }

  validateDiagnosisAdditionalInformation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.showFieldsManualInput()) {
        return null;
      }

      if (!this.showDiagnosisAdditionalInformation()) {
        return null;
      }

      if (!this.diagnosisAutocompleteComponent?.selectedValues.find(diagnosis => this.findDiagnosisById(diagnosis.id, diagnosis.category)?.additionalInformationRequired)) {
        return null;
      }

      return control.value ? null : { required: true }
    }
  }

  validateAccidentDateField(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.showFieldsManualInput() || !this.showAccidentDateField()) {
        return null;
      }
      return control.value ? null : { required: true }
    }
  }

  findDiagnosisById(id: string, category: string): Diagnosis | undefined {
    return diagnosisCategories.find(diagnosis => diagnosis.name === category)?.values.find(diagnosis => diagnosis.id === id);
  }

  // Both errors are recomputed from the current file list here rather than being set inside
  // validateFirstStep(), which the template calls on every change-detection run. Setting them
  // there meant nothing ever cleared them, so the message stayed on screen after the offending
  // file had already been removed and replaced with a valid one.
  onFileListChange() {
    this.showFileSizeError = this.isOverFileSizeLimit();
    this.showFileTypeError = this.hasBlockedFileType();
  }

  isLastPet() {
    return this.selectedPets.length === this.selectedPets.findIndex(p => p.full_contract_number === this.currentPet?.full_contract_number) + 1;
  }

  isButtonDisabled() {
    if (!this.currentPet) {
      return !this.validateFirstStep();
    }

    return this.petInfoForm.invalid || this.diagnosisAutocompleteComponent?.autocompleteInputCtrl.invalid;
  }

  validate() {
    if (this.currentPet) {
      return this.validateForm();
    } else {
      return this.validateFirstStep();
    }
  }

  next() {
    if (!this.validate()) {
      return;
    }

    this.saveCurrentPetData()

    if (this.isLastPet()) {
      return this.submitData()
    }

    this.currentPet = this.selectedPets[this.selectedPets.findIndex(p => p.full_contract_number === this.currentPet?.full_contract_number) + 1];

    this.resetCurrentPetData();

    return true;
  }

  resetCurrentPetData() {
    this.petInfoForm = new FormGroup({
      conditionPreviouslyTreated: new FormControl(false, [this.validateConditionPreviouslyTreated()]),
      dateConditionNoticed: new FormControl(null, [this.validateConditionNoticed()]),
      dateConditionFirstTreated: new FormControl(null, [this.validateConditionFirstTreated()]),
      thirdPartyFault: new FormControl(false, [Validators.required]),
      additionalInformation: new FormControl(null),
      diagnosisAdditionalInformation: new FormControl(null, [this.validateDiagnosisAdditionalInformation()]),
      accidentDateField: new FormControl(null, [this.validateAccidentDateField()]),
      isItFollowUp: new FormControl(false),
      precedentClaimId: new FormControl<string | Claim>('', [this.validatePrecedentClaimId()]),
    });

    this.diagnosisAutocompleteComponent?.reset();

    this.wireManualInputRevalidation();

    this.precedentClaimsList = this.dataStorageService.getAllData.find(pet => pet.full_contract_number === this.currentPet?.full_contract_number)?.claims?.filter(claim => !claim.precedent_claim_id && claim.type !== 'Prävention') || []
  }

  // The diagnosis-driven fields (accident date, first-notice-of-condition, previously-treated,
  // additional info) are gated by showFieldsManualInput(), which depends on isItFollowUp /
  // precedentClaimId rather than on those controls' own values. Re-run their validators when
  // the follow-up state changes so isButtonDisabled() and inline errors update immediately
  // instead of only on the next validateForm() call.
  private wireManualInputRevalidation() {
    const revalidate = () => {
      ['diagnosisAdditionalInformation', 'accidentDateField', 'dateConditionNoticed', 'conditionPreviouslyTreated', 'dateConditionFirstTreated']
        .forEach(controlName => this.petInfoForm.get(controlName)?.updateValueAndValidity({ emitEvent: false }));
    };

    this.petInfoForm.get('isItFollowUp')?.valueChanges.subscribe(revalidate);
    this.petInfoForm.get('precedentClaimId')?.valueChanges.subscribe(revalidate);
  }

  saveCurrentPetData() {
    if (!this.currentPet) {
      this.dataToSubmit = {
        vetInvoice: this.vetInvoice.fileList[0],
        petsData: []
      }
      return;
    }

    if (this.dataToSubmit) {
      this.dataToSubmit.petsData.push({
        ...this.petInfoForm.value,
        conditionPreviouslyTreated: this.showFieldsManualInput() ? this.petInfoForm.value.conditionPreviouslyTreated : null,
        precedentClaimId: typeof this.petInfoForm.value.precedentClaimId === 'string' ? this.petInfoForm.value.precedentClaimId : this.petInfoForm.value.precedentClaimId?.identifier,
        diagnosis: this.diagnosisAutocompleteComponent.selectedValues.map(({ id, category }) => ({ id, category })),
      })
    }
  }

  async submitData() {
    const loader = this.loadingService.open();
    let response: FnolSubmissionResponse | undefined;
    try {
      response = await this.apiHttpService.sendFnolRequest({
        selectedPets: [...new Set(this.selectedPets.map(p => p.base_number))],
        customer_id: this.selectedPets.find(p => p.customer_id)?.customer_id,
        vetInvoice: this.dataToSubmit?.vetInvoice.data64.split(',')[1],
        // The base64 payload carries no name, so without this the file is stored under a
        // bare UUID with no extension. The backend falls back to sniffing the content type
        // when it is absent.
        vetInvoiceName: this.dataToSubmit?.vetInvoice.name,
        petInfo: this.dataToSubmit?.petsData
      });
    } catch (e) {
      console.error(e);
      loader.close();

      // next() pushed the current pet into petsData before this call. The form
      // still shows that pet, so drop the entry: the next attempt pushes it again.
      this.dataToSubmit?.petsData.pop();
      this.showSubmitFailModal();
      return;
    }

    // The backend does not process a failed submission again when it gets the
    // same data, so a retry cannot help. The user must change the data.
    if (response?.duplicate && response.status === 'FAILED') {
      loader.close();
      this.dataToSubmit?.petsData.pop();
      this.showDuplicateFailedModal();
      return;
    }

    this.initStateHash = this.hashPetFnolFields();

    await this.reloadActiveFnolRequests();
    loader.close();

    const successKey = response?.duplicate ? 'submit-duplicate' : 'submit';

    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: this.translate.instant(`pet-details.modals.${successKey}.title`),
        message: this.translate.instant(`pet-details.modals.${successKey}.message`),
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: this.translate.instant('pet-details.modals.submit.button-text'),
      } as GeneralConfirmationModalData
    }).afterClosed().subscribe(() => {
      this.router.navigate(['calingo', 'pet', 'insurance']);
    });
  }

  // The form stays as the user filled it, so "Submit" sends the same claim again.
  private showSubmitFailModal() {
    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: this.translate.instant('pet-details.modals.submit-fail.title'),
        message: this.translate.instant('pet-details.modals.submit-fail.message'),
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: this.translate.instant('pet-details.modals.submit-fail.button-text'),
      } as GeneralConfirmationModalData
    });
  }

  private showDuplicateFailedModal() {
    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: this.translate.instant('pet-details.modals.submit-duplicate-failed.title'),
        message: this.translate.instant('pet-details.modals.submit-duplicate-failed.message'),
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: this.translate.instant('pet-details.modals.submit-duplicate-failed.button-text'),
      } as GeneralConfirmationModalData
    });
  }

  // The submitted request only shows up in the list once the backend has processed it,
  // so give it a moment before pulling the refreshed list.
  private async reloadActiveFnolRequests() {
    await new Promise(resolve => setTimeout(resolve, FNOL_RELOAD_DELAY_MS));

    const fnolRequests = await this.apiHttpService.getActiveFnolRequests();

    if (fnolRequests) {
      this.dataStorageService.fnolRequests = fnolRequests;
    }
  }

  scrollToFirstInvalidControl() {
    const firstInvalidControl = this.petInfoFormElement.nativeElement.querySelector(
      "form .ng-invalid"
    );

    if (firstInvalidControl) {
      firstInvalidControl.scrollIntoView({ block: "center", behavior: "smooth" });

      if ((firstInvalidControl as HTMLElement).focus !== undefined) {
        setTimeout(() => (firstInvalidControl as HTMLElement).focus(), 1000)
      }
    } else {
      this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  canDeactivate(): boolean | Observable<boolean> {
    const translations = this.translate.instant(['pet-details.modals.leave-unsaved.title', 'pet-details.modals.leave-unsaved.message']);
    return !this.isFormChanged() || this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['pet-details.modals.leave-unsaved.title'],
        message: translations['pet-details.modals.leave-unsaved.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      } as GeneralConfirmationModalData
    }).afterClosed().pipe(map((result: GeneralConfirmationModalResult) => {
      return result === GeneralConfirmationModalResult.Confirm;
    }));
  }

  navigateBack() {
    this.router.navigate(['calingo', 'pet', 'insurance']);
  }

  addPet() {
    const defaultConfig = {
      width: '100%',
      maxWidth: 'min(100vw, 1000px)',
      height: '100%',
      panelClass: 'full-screen-dialog'
    }

    const desktopConfig = {
      closeOnNavigation: false,
      panelClass: 'side-modal-container',
      position: {
        top: '0',
        right: '0'
      },
      height: '100vh',
      width: '430px'
    }

    let dialogRef = this.dialogService.openDialog(SelectPetCardsModalComponent, {
      data: { selectedContracts: this.selectedPets.map(contract => contract.full_contract_number) },
      ...(this.isDesktop ? desktopConfig : defaultConfig),
    });
    dialogRef.afterClosed().subscribe((selectedPets) => {
      this.selectedPets = selectedPets;
    });
  }
}
